import { TicketOptionWithQuantity } from '@repo/types';
import { ValidationContext, QuantityInputItem } from './types';
import { getEffectiveCapacity, hasActiveWaitingList, isUnboundedCapacity } from './utils';

/**
 * Calculate base maximum quantity for a ticket
 *
 * Waiting List Support:
 * - If any product instance has hasWaitingList: true AND isClosedForBooking: true,
 *   capacity limits are ignored and max = Number.MAX_VALUE (unlimited)
 * - Waiting list bypass only applies when capacity is full (isClosedForBooking)
 *
 * Fallback chain:
 * 1. Check for waiting list + closed for booking (if both true, return unlimited)
 * 2. Use effective capacity (ticket/product instance capacities)
 * 3. If undefined/null/NaN, default to Number.MAX_VALUE (unlimited)
 *
 * @param ticket - Ticket to calculate max for
 * @returns Maximum quantity
 */
export const calculateBaseMax = (ticket: TicketOptionWithQuantity): number => {
    if (hasActiveWaitingList(ticket)) {
        return Number.MAX_VALUE;
    }

    let effectiveCapacity = getEffectiveCapacity(ticket);

    if (effectiveCapacity === null || effectiveCapacity === undefined || isNaN(effectiveCapacity)) {
        effectiveCapacity = Number.MAX_VALUE;
    }

    if (ticket.occupancy <= 0) {
        return 0;
    }

    const max = Math.floor(effectiveCapacity / ticket.occupancy);

    return Math.max(0, max);
};

/**
 * Calculate maximum considering shared product instance capacity
 * Distributes capacity across tickets sharing the same product instance
 *
 * This is for NON-collection mode - regular activities where multiple
 * ticket types share the same product instance capacity.
 *
 * @param ticket - Ticket to calculate max for
 * @param allTickets - All tickets in the booking
 * @returns Maximum quantity considering shared instance capacity
 */
export const calculateSharedInstanceMax = (
    ticket: TicketOptionWithQuantity,
    allTickets: TicketOptionWithQuantity[],
): number => {
    const baseMax = calculateBaseMax(ticket);

    if (isUnboundedCapacity(baseMax)) {
        return baseMax;
    }

    if (ticket.occupancy <= 0) {
        return 0;
    }

    const ticketPIIds = new Set(ticket.productInstances.map((pi) => pi.id));

    const consumedByOthers = allTickets
        .filter(
            (t) =>
                t.ticketCategoryId !== ticket.ticketCategoryId &&
                t.productInstances.some((pi) => ticketPIIds.has(pi.id)),
        )
        .reduce((sum, t) => sum + t.quantity * t.occupancy, 0);

    const effectiveCapacity = getEffectiveCapacity(ticket);
    const remainingCapacity = effectiveCapacity - consumedByOthers;

    return Math.max(0, Math.floor(remainingCapacity / ticket.occupancy));
};

/**
 * Calculate maximum with collection mode distribution
 * Distributes maxEntrantsPerBooking across multiple ticket types
 *
 * @param ticket - Ticket to calculate max for
 * @param allTickets - All tickets in the collection
 * @param context - Validation context (provides maxEntrantsPerBooking)
 * @returns Maximum quantity considering collection distribution
 */
export const calculateCollectionMax = (
    ticket: TicketOptionWithQuantity,
    allTickets: TicketOptionWithQuantity[],
    context: ValidationContext,
): number => {
    if (context.maxEntrantsPerBooking === null) {
        return calculateBaseMax(ticket);
    }

    const consumedByOthers = allTickets
        .filter((t) => t.ticketCategoryId !== ticket.ticketCategoryId)
        .reduce((sum, t) => sum + t.quantity * t.occupancy, 0);

    const availableCapacity = context.maxEntrantsPerBooking - consumedByOthers;

    const maxFromAvailable = Math.max(0, Math.floor(availableCapacity / ticket.occupancy));

    const baseMax = calculateBaseMax(ticket);

    return Math.min(maxFromAvailable, baseMax);
};

/**
 * Calculate maximum considering shared resource capacity across collection-mode siblings.
 *
 * SB-933: when sibling availability entries share a project_collection_id, the
 * merged product instance's `capacity` reflects seats remaining on the shared
 * physical resource. This may be tighter than the collection-level ceilings,
 * and must bind as a TOTAL across all sibling ticket options. Subtract
 * siblings' consumption from `sharedResourceCapacity`, divide by occupancy,
 * floor at 0, then min against base.
 *
 * @param ticket - Ticket to calculate max for
 * @param allTickets - All tickets in the booking
 * @param context - Validation context (provides sharedResourceCapacity)
 * @returns Maximum quantity considering shared resource distribution
 */
export const calculateCollectionResourceMax = (
    ticket: TicketOptionWithQuantity,
    allTickets: TicketOptionWithQuantity[],
    context: ValidationContext,
): number => {
    if (context.sharedResourceCapacity === null) {
        return calculateBaseMax(ticket);
    }

    if (ticket.occupancy <= 0) {
        return 0;
    }

    const consumedByOthers = allTickets
        .filter((t) => t.ticketCategoryId !== ticket.ticketCategoryId)
        .reduce((sum, t) => sum + t.quantity * t.occupancy, 0);

    const availableCapacity = context.sharedResourceCapacity - consumedByOthers;

    const maxFromAvailable = Math.max(0, Math.floor(availableCapacity / ticket.occupancy));

    const baseMax = calculateBaseMax(ticket);

    return Math.min(maxFromAvailable, baseMax);
};

/**
 * Calculate maximum for joined trip with global capacity
 * Considers global max, consumed capacity, and per-booking limits
 *
 * @param ticket - Ticket to calculate max for
 * @param allTickets - All tickets in the booking
 * @param context - Validation context (provides globalMaxEntrants, maxEntrantsPerBooking, currentConsumedCapacity)
 * @returns Maximum quantity considering joined trip capacity
 */
export const calculateJoinedTripMax = (
    ticket: TicketOptionWithQuantity,
    allTickets: TicketOptionWithQuantity[],
    context: ValidationContext,
): number => {
    if (context.globalMaxEntrants === null) {
        return calculateBaseMax(ticket);
    }

    if (ticket.occupancy <= 0) {
        return 0;
    }

    const consumedByOthers = allTickets
        .filter((t) => t.ticketCategoryId !== ticket.ticketCategoryId)
        .reduce((sum, t) => sum + t.quantity * t.occupancy, 0);

    const availableGlobalCapacity = context.globalMaxEntrants - context.currentConsumedCapacity;

    const availableForThisBooking = availableGlobalCapacity - consumedByOthers;

    let maxFromAvailable = Math.max(0, Math.floor(availableForThisBooking / ticket.occupancy));

    if (context.maxEntrantsPerBooking !== null) {
        const availablePerBooking = context.maxEntrantsPerBooking - consumedByOthers;
        const maxFromPerBooking = Math.max(0, Math.floor(availablePerBooking / ticket.occupancy));
        maxFromAvailable = Math.min(maxFromAvailable, maxFromPerBooking);
    }

    const baseMax = calculateBaseMax(ticket);

    return Math.min(maxFromAvailable, baseMax);
};

/**
 * Calculate minimum quantity for a ticket (base calculation)
 *
 * Returns the highest minEntrants across ticket option and product instances.
 * This is used for display/UI purposes when no sharing context is available.
 *
 * @param ticket - Ticket to calculate min for
 * @returns Minimum quantity (0 if no minimum specified)
 */
export const calculateMin = (ticket: TicketOptionWithQuantity): number => {
    let min = ticket.minEntrants ?? 0;

    if (ticket.minEntrants !== null) {
        return min;
    }

    for (const pi of ticket.productInstances) {
        if (pi.minEntrants !== null && pi.minEntrants > min) {
            min = pi.minEntrants;
        }
    }

    return min;
};

/**
 * Calculate minimum quantity considering shared product instances
 *
 * When multiple tickets share the same product instance, the minEntrants
 * constraint is TOTAL-based, so individual ticket min should be 0.
 *
 * @param ticket - Ticket to calculate min for
 * @param allTickets - All tickets in the booking
 * @returns Minimum quantity (0 if shared, otherwise uses calculateMin)
 */
export const calculateSharedInstanceMin = (
    ticket: TicketOptionWithQuantity,
    allTickets: TicketOptionWithQuantity[],
): number => {
    if (ticket.minEntrants !== null) {
        return ticket.minEntrants;
    }

    const ticketPIIds = new Set(ticket.productInstances.map((pi) => pi.id));
    const hasSharedInstance = allTickets.some(
        (t) =>
            t.ticketCategoryId !== ticket.ticketCategoryId &&
            t.productInstances.some((pi) => ticketPIIds.has(pi.id)),
    );

    if (hasSharedInstance) {
        return 0;
    }

    return calculateMin(ticket);
};

/**
 * Apply personsMax limit to input items when no product instance is selected.
 * Distributes the limit across all ticket types.
 *
 * This should only be used before a date/product instance is selected.
 * Once a product instance is selected, use the actual capacity from the product instance.
 *
 * @param items - Input items to apply limit to
 * @param personsMax - Maximum total persons allowed
 * @returns New array with max values capped by personsMax distribution
 */
export const applyPersonsMaxLimit = (
    items: readonly QuantityInputItem[],
    personsMax: number,
): QuantityInputItem[] => {
    return items.map((item) => {
        const consumedByOthers = items
            .filter((i) => i.id !== item.id)
            .reduce((sum, i) => sum + i.value, 0);
        const availableFromPersonsMax = personsMax - consumedByOthers;
        return {
            ...item,
            max: Math.min(item.max, Math.max(0, availableFromPersonsMax)),
        };
    });
};
